/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';
import {
  TailDetailsPanelProps,
  PANEL_HEADER_HEIGHT_PX,
  TAIL_DETAILS_PANEL_WIDTH,
  TAIL_DETAILS_TAIL_NUMBER_TOOLTIP_WIDTH_PX,
} from './TailDetailsPanel';
import TruncateWithTooltip from '../../common/TruncateWithTooltip';
import StyledProps from '../../common/theme/AppThemeInterface';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../utils/StringUtils';
import { getTerminalDisplayName } from '../../../utils/TerminalUtilities';
import isNil from 'lodash/isNil';
import { Close, MyLocation, Block, Straighten } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import Terminal, { TerminalDetails } from '../../../shared/entities/terminal';
import { getMostRecentTerminalInAircraft } from '../../../utils/ConnectionStatusUtils';
import { isNotNullOrUndefined } from '../../../utils/NullUtils';
import appTheme from '../../common/theme/appTheme.json';
import { shallowEqual, useSelector } from 'react-redux';
import { selectOverlaysCurrentContext } from '../../../store';
import { OverlaysContextsEnum } from '../../../store/maps/reducer';

interface TailDetailsPanelHeaderProps extends TailDetailsPanelProps {}

export const TailDetailsPanelHeaderTailLabel = styled.div`
  height: 28px;
  max-width: 228px;
  min-width: 228px;
  width: 228px;
  color: white;
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  font-size: 20px;
  font-weight: 300;
  line-height: 28px;
  margin-left: 20px;
  position: relative;
  display: inline-block;
`;

interface TailDetailsPanelHeaderDivProps {
  height: number;
  width: number;
}

export const TailDetailsPanelHeaderDiv = styled.div`
  ${({ height, width }: TailDetailsPanelHeaderDivProps) => css`
    height: ${height ? height + 'px' : '57px'};
    width: ${width ? width + 'px' : '360px'};
  `};
  display: flex;
  box-sizing: border-box;
  position: relative;
  top: 1px;
  z-index: 2;
  align-items: center;
`;

export const panToTailOnMap = (
  tailDetailsPanelTerminal: TerminalDetails | any,
  terminals: {
    [key: string]: Terminal;
  },
  selectedDatapathUuidFleetView: string | undefined,
  setMapZoomLatLngFleetView: Function,
) => {
  let tailDetailTerminal: Terminal | TerminalDetails;
  // tail details request error so pull info from terminal request
  if (!tailDetailsPanelTerminal) {
    tailDetailTerminal = terminals[selectedDatapathUuidFleetView as string];
  } else {
    tailDetailTerminal = tailDetailsPanelTerminal;
  }
  let onlineTerminal = getMostRecentTerminalInAircraft(tailDetailTerminal, terminals);
  if (
    onlineTerminal &&
    isNotNullOrUndefined(onlineTerminal.latitudeDeg) &&
    isNotNullOrUndefined(onlineTerminal.longitudeDeg)
  ) {
    setMapZoomLatLngFleetView({ lat: onlineTerminal.latitudeDeg, lng: onlineTerminal.longitudeDeg });
  }
};

const TailDetailsPanelHeader = ({
  selectedDatapathUuidFleetView,
  closeDetailsPanel,
  clearFlightPath,
  tailDetailsPanelTerminal,
  terminals,
  isRequestingTailDetailsPanelTerminal,
  requestTailDetailsPanelTerminalError,
  hiddenTerminals,
  setMapZoomLatLngFleetView,
  toggleMapRulerTool,
  mapRulerToolShown,
  mapRulerToolFollowAircraft,
  hideMapRulerTool,
}: TailDetailsPanelHeaderProps) => {
  let terminalName = getTerminalDisplayName(tailDetailsPanelTerminal);
  const overlaysCurrentContext = useSelector(selectOverlaysCurrentContext, shallowEqual);

  const onDetailsPanelCloseClicked = useCallback(() => {
    closeDetailsPanel();
    clearFlightPath();

    // Close the ruler tool if was enabled and following an aircraft
    if (mapRulerToolShown && mapRulerToolFollowAircraft) {
      hideMapRulerTool();
    }
  }, [closeDetailsPanel, clearFlightPath, mapRulerToolShown, mapRulerToolFollowAircraft, hideMapRulerTool]);

  return (
    <TailDetailsPanelHeaderDiv height={PANEL_HEADER_HEIGHT_PX} width={TAIL_DETAILS_PANEL_WIDTH}>
      <React.Fragment>
        <React.Fragment>
          {tailDetailsPanelTerminal && !isRequestingTailDetailsPanelTerminal ? (
            <React.Fragment>
              <TailDetailsPanelHeaderTailLabel
                data-test="tail__details__panel__header__label"
                className="tail__details__panel__header__label"
              >
                <TruncateWithTooltip
                  text={terminalName ? terminalName : EMPTY_FIELD_STRING_PLACEHOLDER}
                  tooltipMaxWidthPx={TAIL_DETAILS_TAIL_NUMBER_TOOLTIP_WIDTH_PX}
                />
              </TailDetailsPanelHeaderTailLabel>
            </React.Fragment>
          ) : null}
        </React.Fragment>
        {requestTailDetailsPanelTerminalError === undefined &&
        !isRequestingTailDetailsPanelTerminal &&
        !isNil(tailDetailsPanelTerminal) &&
        tailDetailsPanelTerminal.restrictionEnabled ? (
          <div className="tail__details__panel____header__restricted__icon__container">
            <Block
              data-test={`tail-details-panel__block-icon__${tailDetailsPanelTerminal.datapathUuid}`}
              className="tail__details__panel____header__restricted__icon"
            />
          </div>
        ) : requestTailDetailsPanelTerminalError === undefined &&
          !isRequestingTailDetailsPanelTerminal &&
          !isNil(tailDetailsPanelTerminal) &&
          !isNil(tailDetailsPanelTerminal.latitudeDeg) &&
          !isNil(tailDetailsPanelTerminal.longitudeDeg) ? (
          <React.Fragment>
            {overlaysCurrentContext !== OverlaysContextsEnum.TRANSPORT_MONITORING ? (
              <Tooltip
                arrow
                title="Measure Distance From Aircraft"
                placement="bottom"
                componentsProps={{
                  tooltip: {
                    sx: { backgroundColor: appTheme.tooltipBackgroundColor },
                  },
                  arrow: {
                    sx: { color: appTheme.tooltipBackgroundColor },
                  },
                }}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -10],
                        },
                      },
                    ],
                  },
                }}
              >
                <IconButton
                  className="tail__details__panel____header__ruler__tool__button"
                  onClick={() => {
                    toggleMapRulerTool();
                  }}
                  disabled={hiddenTerminals.includes(tailDetailsPanelTerminal.modemUuid)}
                >
                  <Straighten className="tail__details__panel____header__ruler__tool__icon" />
                </IconButton>
              </Tooltip>
            ) : null}
            <IconButton
              className="tail__details__panel____header__pan__to__terminal__button"
              onClick={() => {
                panToTailOnMap(
                  tailDetailsPanelTerminal,
                  terminals,
                  selectedDatapathUuidFleetView,
                  setMapZoomLatLngFleetView,
                );
              }}
              disabled={hiddenTerminals.includes(tailDetailsPanelTerminal.modemUuid)}
            >
              <MyLocation className="tail__details__panel____header__pan__to__terminal__icon" />
            </IconButton>
          </React.Fragment>
        ) : null}
        <IconButton className="tail__details__panel__header__collapse__button" onClick={onDetailsPanelCloseClicked}>
          <Close className="tail__details__panel__header__collapse__icon" />
        </IconButton>
      </React.Fragment>
    </TailDetailsPanelHeaderDiv>
  );
};

export default TailDetailsPanelHeader;
